import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import type { UploadFile } from 'antd/lib/upload/interface';
import { fetchApi } from 'core/utils/api';
import { handleError } from 'core/utils/helpers';

// Turn any thrown error into a clear, user-facing message: the API's message
// when it sent one, otherwise a friendly connectivity fallback (handleError
// returns false for network / non-HTTP errors, which would otherwise surface
// nothing to the guest).
const GUEST_ERROR_FALLBACK = 'We could not reach the server. Please check your connection and try again.';
const describeGuestError = (error: unknown): string => {
    const message = handleError(error);
    return message === false ? GUEST_ERROR_FALLBACK : message;
};

/**
 * Public (token-gated, no auth) guest booking thunks. They call the /booking
 * token endpoints and return the same `{ visitRequest, stepData }` shape as the
 * authenticated flow, so the shared visitRequestForm slice + step components can
 * be reused.
 */

export const loadGuestBooking = createAsyncThunk(
    'guestBooking/summary',
    async (token: string, { rejectWithValue }) => {
        try {
            const response = await fetchApi({ method: 'GET', url: `booking/${token}` });
            return response.data;
        } catch (error: unknown) {
            // The GuestBooking page renders its own invalid-link message, so no
            // toast here — just reject with a clear reason.
            return rejectWithValue(describeGuestError(error));
        }
    },
);

export const loadGuestStep = createAsyncThunk(
    'guestBooking/loadStep',
    async (payload: { token: string, step: number }, { rejectWithValue }) => {
        try {
            const response = await fetchApi({ method: 'GET', url: `booking/${payload.token}/step/${payload.step}` });
            return response.data;
        } catch (error: unknown) {
            const description = describeGuestError(error);
            notification.error({ message: 'Error', description });
            return rejectWithValue(description);
        }
    },
);

export const updateGuestStep = createAsyncThunk(
    'guestBooking/updateStep',
    async (payload: { token: string, data: object }, { rejectWithValue }) => {
        try {
            const response = await fetchApi({ method: 'POST', url: `booking/${payload.token}/step`, body: payload.data });
            return response.data;
        } catch (error: unknown) {
            const description = describeGuestError(error);
            notification.error({ message: 'Error', description });
            return rejectWithValue(description);
        }
    },
);

export const uploadGuestAttachment = createAsyncThunk(
    'guestBooking/uploadAttachment',
    async (payload: { token: string, attachment: UploadFile }, { rejectWithValue }) => {
        const { token, attachment } = payload;
        const formData = new FormData();
        try {
            const file = attachment.originFileObj;
            if (file) {
                formData.append('file', file);
            }
            const response = await fetchApi({
                method: 'POST',
                url: `booking/${token}/upload-attachment`,
                body: formData,
            });

            notification.success({
                message: 'Success',
                description: 'File uploaded successfully.',
            });

            return response.data;
        } catch (error: unknown) {
            const description = describeGuestError(error);
            notification.error({ message: 'Error', description });
            return rejectWithValue(description);
        }
    },
);

export const submitGuestBooking = createAsyncThunk(
    'guestBooking/submit',
    async (payload: { token: string, declaration: object }, { rejectWithValue }) => {
        try {
            const response = await fetchApi({
                method: 'POST',
                url: `booking/${payload.token}/submit`,
                // ReviewStep reads $request->declaration, so it must be nested.
                body: { declaration: payload.declaration },
            });

            notification.success({
                message: 'Submitted',
                description: 'Your visit request has been submitted.',
            });

            return response.data;
        } catch (error: unknown) {
            const description = describeGuestError(error);
            notification.error({ message: 'Error', description });
            return rejectWithValue(description);
        }
    },
);
